import type { MachineStatus, Telemetry } from '@bridgewerk/shared';
import { useMemo } from 'react';

const HOUR_COUNT = 24;

const COLOR: Record<MachineStatus, string> = {
  running: 'var(--color-green)',
  idle: 'var(--color-yellow)',
  alarm: 'var(--color-red)',
  offline: '#1A3048',
};

export interface StatusTimelineProps {
  readonly points: Telemetry[] | undefined;
  readonly loading?: boolean;
}

interface HourBucket {
  readonly hour: number;
  readonly status: MachineStatus | null;
}

export function StatusTimeline({ points, loading }: StatusTimelineProps): JSX.Element {
  const buckets = useMemo(() => bucketize(points ?? []), [points]);
  const hasData = (points?.length ?? 0) > 0;

  return (
    <section className="rounded-md border border-border bg-card p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          Status timeline — last 24 hours
        </h2>
      </header>
      {loading && !points ? (
        <div className="h-8 animate-shimmer rounded bg-card-2" />
      ) : !hasData ? (
        <p className="text-xs text-text-dim text-center py-4">No telemetry in this window.</p>
      ) : (
        <>
          <div
            role="img"
            aria-label="Hour-by-hour machine status for the last 24 hours"
            className="grid h-8 rounded-sm overflow-hidden"
            style={{ gridTemplateColumns: `repeat(${HOUR_COUNT}, minmax(0, 1fr))` }}
          >
            {buckets.map((b) => (
              <div
                key={b.hour}
                title={`${formatHour(b.hour)}: ${b.status ?? 'no data'}`}
                style={{
                  backgroundColor: b.status ? COLOR[b.status] : 'var(--color-border)',
                  opacity: b.status ? 1 : 0.4,
                }}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] font-mono text-muted">
            <span>−24h</span>
            <span>−12h</span>
            <span>now</span>
          </div>
          <Legend />
        </>
      )}
    </section>
  );
}

function Legend(): JSX.Element {
  const items: { status: MachineStatus; label: string }[] = [
    { status: 'running', label: 'Running' },
    { status: 'idle', label: 'Idle' },
    { status: 'alarm', label: 'Alarm' },
    { status: 'offline', label: 'Offline' },
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-3 text-[10px] font-mono text-text-dim">
      {items.map((i) => (
        <span key={i.status} className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-sm"
            style={{ backgroundColor: COLOR[i.status] }}
          />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function bucketize(points: Telemetry[]): HourBucket[] {
  const now = Date.now();
  const buckets: (Map<MachineStatus, number> | null)[] = Array.from(
    { length: HOUR_COUNT },
    () => null,
  );
  for (const p of points) {
    const t = new Date(p.timestamp).getTime();
    const hoursAgo = Math.floor((now - t) / (60 * 60 * 1000));
    if (hoursAgo < 0 || hoursAgo >= HOUR_COUNT) continue;
    const idx = HOUR_COUNT - 1 - hoursAgo;
    let m = buckets[idx];
    if (!m) {
      m = new Map();
      buckets[idx] = m;
    }
    m.set(p.status, (m.get(p.status) ?? 0) + 1);
  }
  return buckets.map((m, hour) => ({
    hour,
    status: m ? mostFrequent(m) : null,
  }));
}

function mostFrequent(m: Map<MachineStatus, number>): MachineStatus {
  let best: MachineStatus = 'offline';
  let bestN = -1;
  for (const [k, v] of m) {
    if (v > bestN) {
      best = k;
      bestN = v;
    }
  }
  return best;
}

function formatHour(idx: number): string {
  const hoursAgo = HOUR_COUNT - 1 - idx;
  if (hoursAgo === 0) return 'now';
  return `${hoursAgo}h ago`;
}
